<template>
  <div>
    <table-wrapper
      v-model:filters="filters"
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="vgbmData"
      :loading="isLoading"
      :mode="TableMode.LAZY"
      row-clickable
      @filtering-reset="handleFilterReset"
      @row-click="emitKksClickedEvent"
    >
    </table-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import {
    IProjectVgbmRequestDto,
    IProjectVgbmResponseDto,
    IVgbkListElementDto,
    Order,
  } from 'types';
  import { VgbmService } from '@/services';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';

  const columnWidths = {
    kks: '250px',
    vgbk: '250px',
    vgbmClass: '150px',
  };

  const props = defineProps<{
    projectId: string;
    selectedVgbk: IVgbkListElementDto | null;
  }>();

  const emit = defineEmits<{
    (e: 'filtering-reset'): void;
    (e: 'kks-clicked', kksId: string): void;
    (e: 'results-count-updated', count: number): void;
  }>();

  const vgbmData = ref<IProjectVgbmResponseDto[]>([]);

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const TABLE_NAMESPACE = 'vt';
  const { filters, filterParams, pageOptions, isLoading, fetch } =
    useTableWrapper<IProjectVgbmResponseDto, IProjectVgbmRequestDto>(
      TableMode.LAZY,
      'kks',
      fetchVgbmData,
      Order.ASC,
      true,
      TABLE_NAMESPACE,
    );

  const tableFields = computed(() => {
    const builder = new TableFieldsBuilder<IProjectVgbmResponseDto>({
      labelTranslationKey: 'vgbmTable',
      width: columnWidths.vgbmClass,
      cellClassGetter: getCellClass,
    })
      .addField('kks', {
        label: 'KKS',
        width: columnWidths.kks,
      })
      .addField('vgbk', {
        width: columnWidths.vgbk,
      });
    VgbmService.getVgbmClassesFromVgbmList(vgbmData.value).forEach(
      (vgbmClass) => {
        builder.addField(vgbmClass, {
          label: vgbmClass,
        });
      },
    );
    return builder.build();
  });

  const filterConfiguration = computed(() => {
    return new TableFiltersBuilder<
      IProjectVgbmResponseDto,
      IProjectVgbmRequestDto
    >({
      translationKey: 'vgbmTable',
    })
      .addInputFilter('kks', {
        width: parseInt(columnWidths.kks) * 0.75 + 'px',
        customLabel: 'KKS',
      })
      .addInputFilter('vgbk', {
        width: parseInt(columnWidths.vgbk) * 0.75 + 'px',
        simpleLabel: true,
      })
      .build();
  });

  async function fetchVgbmData() {
    try {
      const paginationResponse =
        await VgbmService.getPaginatedVgbmListForProject(
          props.projectId,
          pageOptions.value.page,
          pageOptions.value.perPage,
          filterParams.value,
          !!props.selectedVgbk,
        );
      vgbmData.value = paginationResponse.data;
      pageOptions.value.rows = paginationResponse.meta.itemCount;
      emit('results-count-updated', paginationResponse.meta.itemCount);
    } catch {
      createErrorToast(t('vgbmTable.errors.errorGettingVgbm'));
    }
  }

  function handleFilterReset() {
    emit('filtering-reset');
  }

  function getCellClass(
    _value: string | null,
    key: keyof IProjectVgbmResponseDto,
    item: IProjectVgbmResponseDto,
  ): string[] {
    /**
     * If the VGBM in the current column is not present in the VGBK of the
     * current row, the cell should be displayed as disabled.
     */
    if (item[key] === undefined) {
      return ['vgbm-not-in-vgbk'];
    }
    return [];
  }

  function emitKksClickedEvent(data: IProjectVgbmResponseDto) {
    emit('kks-clicked', data.kksId);
  }

  watch(
    () => props.selectedVgbk,
    (newSelectedVgbk) => {
      filters.value.vgbk = newSelectedVgbk?.class;
      fetch();
    },
  );
</script>

<style lang="scss" scoped>
  :deep(.vgbm-not-in-vgbk) {
    background-color: $form-control-disabled-color;
  }
</style>
